import { getFixedT } from '@/hooks/useTranslation';

/**
 * Bibliothèque Problème métier → Template Expert.
 * Source : TaleScan V0.5, §4 et §6 (Univers/Domaines + Problèmes métier).
 * Volontairement statique : cette liste est censée rester "peu nombreuse et
 * stable" (V0.5 §12) — on migrera vers une source distante seulement si la
 * gouvernance des templates l'exige.
 *
 * Display copy (question/templateName) lives in src/locales/{en,fr,ar}.json
 * under surveyBuilder.taleScan.problems.items.<problemId> — not here, since
 * translations aren't available at module scope. Resolve it via
 * getProblemLabels(problemId, t) from a component that has already called
 * useTranslation().
 */
export const taleScanDomains = {
  marketing: 'Marketing / Consumer Insights',
  rh: 'RH / Employee Insights',
  b2b: 'B2B / Business Insights',
  opinion: 'Opinion publique / Public Insights',
  innovation: 'Innovation',
  evaluation: 'Évaluation / Programmes / Projets',
  communication: 'Communication / Institutionnel',
};

export const taleScanProblems = [
  {
    problemId: 'buying',
    domainId: 'marketing',
    status: 'PUBLISHED',
    category: 'GENERAL_MARKET_RESEARCH',
  },
  {
    problemId: 'brand',
    domainId: 'marketing',
    status: 'DRAFT',
    category: 'BRAND_TRACKING_OR_AWARENESS',
  },
  { problemId: 'customer-experience', domainId: 'marketing', status: 'DRAFT' },
  { problemId: 'satisfaction', domainId: 'marketing', status: 'DRAFT' },
  { problemId: 'usage', domainId: 'marketing', status: 'DRAFT' },
  { problemId: 'loyalty', domainId: 'marketing', status: 'DRAFT' },
  { problemId: 'price', domainId: 'marketing', status: 'DRAFT' },
  { problemId: 'concept', domainId: 'marketing', status: 'DRAFT' },

  // RH / Employee
  { problemId: 'rh-employee-experience', domainId: 'rh', status: 'DRAFT' },
  { problemId: 'rh-engagement', domainId: 'rh', status: 'DRAFT' },
  { problemId: 'rh-employee-satisfaction', domainId: 'rh', status: 'DRAFT' },
  { problemId: 'rh-motivation', domainId: 'rh', status: 'DRAFT' },
  { problemId: 'rh-onboarding', domainId: 'rh', status: 'DRAFT' },
  { problemId: 'rh-training', domainId: 'rh', status: 'DRAFT' },
  { problemId: 'rh-internal-communication', domainId: 'rh', status: 'DRAFT' },
  { problemId: 'rh-retention', domainId: 'rh', status: 'DRAFT' },

  // B2B / Business
  { problemId: 'b2b-needs', domainId: 'b2b', status: 'DRAFT' },
  { problemId: 'b2b-buying-process', domainId: 'b2b', status: 'DRAFT' },
  { problemId: 'b2b-decision-makers', domainId: 'b2b', status: 'DRAFT' },
  { problemId: 'b2b-supplier-selection', domainId: 'b2b', status: 'DRAFT' },
  { problemId: 'b2b-customer-experience', domainId: 'b2b', status: 'DRAFT' },
  { problemId: 'b2b-satisfaction', domainId: 'b2b', status: 'DRAFT' },
  { problemId: 'b2b-retention', domainId: 'b2b', status: 'DRAFT' },
  { problemId: 'b2b-value-proposition', domainId: 'b2b', status: 'DRAFT' },

  // Opinion publique / Public
  { problemId: 'opinion-opinion', domainId: 'opinion', status: 'DRAFT' },
  { problemId: 'opinion-perception', domainId: 'opinion', status: 'DRAFT' },
  { problemId: 'opinion-trust', domainId: 'opinion', status: 'DRAFT' },
  { problemId: 'opinion-priorities', domainId: 'opinion', status: 'DRAFT' },
  { problemId: 'opinion-intentions', domainId: 'opinion', status: 'DRAFT' },
  { problemId: 'opinion-satisfaction', domainId: 'opinion', status: 'DRAFT' },
  { problemId: 'opinion-awareness', domainId: 'opinion', status: 'DRAFT' },
  { problemId: 'opinion-acceptability', domainId: 'opinion', status: 'DRAFT' },

  // Innovation
  { problemId: 'innovation-need-validation', domainId: 'innovation', status: 'DRAFT' },
  { problemId: 'innovation-concept-evaluation', domainId: 'innovation', status: 'DRAFT' },
  { problemId: 'innovation-adoption', domainId: 'innovation', status: 'DRAFT' },
  { problemId: 'innovation-barriers', domainId: 'innovation', status: 'DRAFT' },
  { problemId: 'innovation-feature-prioritization', domainId: 'innovation', status: 'DRAFT' },
  { problemId: 'innovation-value-proposition', domainId: 'innovation', status: 'DRAFT' },

  // Évaluation / Programmes / Projets
  { problemId: 'evaluation-needs-assessment', domainId: 'evaluation', status: 'DRAFT' },
  { problemId: 'evaluation-relevance', domainId: 'evaluation', status: 'DRAFT' },
  { problemId: 'evaluation-satisfaction', domainId: 'evaluation', status: 'DRAFT' },
  { problemId: 'evaluation-beneficiary-experience', domainId: 'evaluation', status: 'DRAFT' },
  { problemId: 'evaluation-effectiveness', domainId: 'evaluation', status: 'DRAFT' },
  { problemId: 'evaluation-accessibility', domainId: 'evaluation', status: 'DRAFT' },
  { problemId: 'evaluation-impact-perception', domainId: 'evaluation', status: 'DRAFT' },
  { problemId: 'evaluation-improvement-priorities', domainId: 'evaluation', status: 'DRAFT' },

  // Communication / Institutionnel
  { problemId: 'communication-message-testing', domainId: 'communication', status: 'DRAFT' },
  { problemId: 'communication-campaign-evaluation', domainId: 'communication', status: 'DRAFT' },
  { problemId: 'communication-recall', domainId: 'communication', status: 'DRAFT' },
  { problemId: 'communication-channel-effectiveness', domainId: 'communication', status: 'DRAFT' },
  { problemId: 'communication-credibility', domainId: 'communication', status: 'DRAFT' },
  { problemId: 'communication-engagement', domainId: 'communication', status: 'DRAFT' },
];

export const getProblemById = (problemId) =>
    taleScanProblems.find((p) => p.problemId === problemId) ?? null;

/**
 * Resolves a problem's display copy in the caller's current UI language.
 * `t` must come from the calling component's own useTranslation() — these
 * labels aren't resolvable at module scope.
 */
export const getProblemLabels = (problemId, t) => ({
  question: t(`surveyBuilder.taleScan.problems.items.${problemId}.question`),
  templateName: t(`surveyBuilder.taleScan.problems.items.${problemId}.templateName`),
});

/**
 * Banque de questions core par problème métier — type de réponse et forme
 * (nb d'options) uniquement ; prête à être mappée sur le payload de création
 * de survey réel une fois ce mapping connu (cf. handleSelect dans
 * TaleScanDashboard.jsx).
 *
 * BUYING : source Tale_Scan_AI_TaleCraft Specification §9.
 * BRAND  : source Modèle_Brand_Perception.docx fourni par le client
 *          (jeu de questions n°2 du document, celui avec un "Type : ..."
 *          explicite sur chaque question — l'autre jeu du même doc n'a pas
 *          de type annoté). À confirmer avec le client si ce n'était pas
 *          le jeu voulu.
 * Tout le reste de la bibliothèque n'a pas encore de contenu réel — un
 * template DRAFT sans banque ici démarre une survey sans questions
 * pré-remplies plutôt que de planter.
 *
 * The question/option TEXT itself lives in src/locales/{en,fr,ar,tn}.json
 * under surveyBuilder.taleScan.questionBanks.<problemId>.<qKey> — French is
 * the source of truth (client's own authored content, verbatim from their
 * documents). EN/AR are machine-translated drafts under
 * questionBanks_draft.* pending native review, so getCoreQuestions falls
 * back to French for any key not yet promoted into the live questionBanks
 * namespace — see resolveCoreQuestionText below.
 */
const CORE_QUESTIONS_BY_PROBLEM = {
  buying: [
    { key: 'q1', responseType: 'single_choice' },
    { key: 'q2', responseType: 'multiple_choice' },
    { key: 'q3', responseType: 'multiple_choice' },
    { key: 'q4', responseType: 'single_choice' },
    { key: 'q5', responseType: 'multiple_choice' },
    { key: 'q6', responseType: 'scale' },
    { key: 'q7', responseType: 'scale' },
    { key: 'q8', responseType: 'multiple_choice' },
  ],
  brand: [
    { key: 'q1', responseType: 'single_choice', optionKeys: ['o1', 'o2', 'o3', 'o4'] },
    // Q2 — type "matrice" dans le doc client (4 lignes, échelle 1-5 partagée) :
    // pas de format "matrice" dans l'app, éclaté en 4 questions star (1-5)
    // séparées (q2..q5), description = les deux pôles de l'axe. À revoir si
    // un vrai type matrice existe côté backend.
    { key: 'q2', responseType: 'rating_1_5' },
    { key: 'q3', responseType: 'rating_1_5' },
    { key: 'q4', responseType: 'rating_1_5' },
    { key: 'q5', responseType: 'rating_1_5' },
    { key: 'q6', responseType: 'single_choice', optionKeys: ['o1', 'o2', 'o3', 'o4', 'o5', 'o6'] },
    { key: 'q7', responseType: 'emoji_scale' }, // réaction instinctive par émojis
    { key: 'q8', responseType: 'single_choice_other', optionKeys: ['o1', 'o2', 'o3', 'o4', 'o5', 'o6'] },
    { key: 'q9', responseType: 'single_choice', optionKeys: ['o1', 'o2', 'o3', 'o4', 'o5', 'o6'] },
    { key: 'q10', responseType: 'scale' },
    { key: 'q11', responseType: 'open_text' },
  ],
};

const QUESTION_BANKS_NAMESPACE = 'surveyBuilder.taleScan.questionBanks';

/**
 * Resolves one i18n key against the chosen-language `t`, falling back to
 * French (the source of truth for this content) when the key isn't live
 * yet — i.e. EN/AR draft translations that haven't been promoted out of
 * questionBanks_draft. `t()` returns the key itself on a miss (see
 * useTranslation.js), which is how we detect that case.
 */
const resolveWithFrenchFallback = (key, t, frenchT) => {
  const value = t(key);
  return value === key ? frenchT(key) : value;
};

const resolveCoreQuestion = (problemId, meta, t, frenchT) => {
  const base = `${QUESTION_BANKS_NAMESPACE}.${problemId}.${meta.key}`;
  const question = {
    text: resolveWithFrenchFallback(`${base}.text`, t, frenchT),
    responseType: meta.responseType,
  };
  if (meta.optionKeys) {
    question.options = meta.optionKeys.map((optionKey) =>
      resolveWithFrenchFallback(`${base}.options.${optionKey}`, t, frenchT)
    );
  }
  return question;
};

const contextualize = (question, context, t, frenchT) => ({
  ...question,
  text: question.text.replace(
    '{product}',
    context?.product?.trim() || resolveWithFrenchFallback(`${QUESTION_BANKS_NAMESPACE}.defaultProductLabel`, t, frenchT)
  ),
});

/**
 * Renvoie la banque de questions core pour un problème, contextualisée, dans
 * la langue choisie par l'utilisateur (`t`, typiquement issu de
 * getFixedT(locale) — voir TaleScanDashboard.jsx). `t` par défaut sur le
 * français si non fourni (comportement historique).
 * Synchrone (plus de mock avec délai — la vraie création de survey portera
 * l'asynchronicité côté API). Renvoie [] si le template n'a pas encore de
 * contenu réel, pour démarrer une survey vide plutôt que planter.
 */
export const getCoreQuestions = (problemId, context = {}, t) => {
  const frenchT = getFixedT('fr');
  const resolvedT = t ?? frenchT;
  const bank = CORE_QUESTIONS_BY_PROBLEM[problemId] ?? [];
  return bank
    .map((meta) => resolveCoreQuestion(problemId, meta, resolvedT, frenchT))
    .map((q) => contextualize(q, context, resolvedT, frenchT));
};

/**
 * Maps our template question shape onto the real survey question shape
 * consumed by CreateSurveyLayout / CreateSurveyStep2 (question_text,
 * format, possible_responses, responseType — see isQuestionGood in
 * survey/new/scratch/index.jsx).
 *
 * format enum: "1-10" | "smile" | "star" | "comment" | "checkbox" | "radio"
 * responseType here means question MEDIA type (TEXT/IMAGE/VIDEO/SOUND),
 * not the answer type — always TEXT for TaleScan-generated questions.
 *
 * IMPORTANT: Buying's single_choice/multiple_choice questions don't have
 * real option text yet (the client's Buying doc only gave the question
 * title, no answer list — cf. taleScanProblems.js note above). Rather than
 * invent plausible-sounding options, those fall back to "comment" (open
 * text) until real options exist — flagged, not silently guessed.
 */
const RESPONSE_TYPE_TO_FORMAT = {
  single_choice: 'radio',
  multiple_choice: 'checkbox',
  single_choice_other: 'radio',
  rating_1_5: 'star',
  scale: '1-10',
  emoji_scale: 'smile',
  open_text: 'comment',
};

export const toSurveyQuestion = (question) => {
  const needsOptions = ['radio', 'checkbox'].includes(RESPONSE_TYPE_TO_FORMAT[question.responseType]);
  const hasOptions = Array.isArray(question.options) && question.options.length > 0;
  const format = needsOptions && !hasOptions ? 'comment' : RESPONSE_TYPE_TO_FORMAT[question.responseType] ?? 'comment';

  return {
    id: crypto.randomUUID(),
    question_text: question.text,
    format,
    responseType: 'TEXT',
    possible_responses: format === 'radio' || format === 'checkbox' ? question.options : [],
    assets: [],
  };
};
